import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Button,
  Checkbox
} from "@heroui/react";
import {
  SpinnerGap,
  User,
  PaperPlaneTilt,
  Check
} from "@phosphor-icons/react";
import { RentalContract } from "shared/types/rental-contracts";
import { Contact } from "shared/types/contacts";
import { ContactType, ContractContact } from "shared/types/rental-contracts";
import { useSession } from "@clerk/clerk-react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { toast } from "react-toastify";

interface MigrationModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedContract: RentalContract | null;
}

export const MigrationModal: React.FC<MigrationModalProps> = ({
  isOpen,
  onClose,
  selectedContract,
}) => {
  const [isRunningMigration, setIsRunningMigration] = useState<{ [key: number]: boolean }>({});
  const [isSendingAll, setIsSendingAll] = useState(false);
  const [migrationStatus, setMigrationStatus] = useState<{ [key: number]: boolean }>({});
  const [isLoadingStatus, setIsLoadingStatus] = useState(true);
  const { session } = useSession();

  const getTenantContacts = (contract: RentalContract): Contact[] => {
    if (!contract.contractContacts) return [];
    
    return contract.contractContacts
      .filter((contact: ContractContact) => contact.contactType === ContactType.TENANT 
      || contact.contactType === ContactType.INHABITANT)
      .map((contact: any) => contact.relatedContact)
      .filter((contact: Contact) => contact != null);
  };

  const checkMigrationStatus = async (contactId: number): Promise<boolean> => {
    try {
      const response = await fetchWithAuth(
        `${API_URL}/tenants-migration/is-migrated/${contactId}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          }
        },
        session
      );

      if (response.ok) {
        const result = await response.json();
        return result === true;
      }
      return false;
    } catch (error) {
      console.error('Error checking migration status:', error);
      return false;
    }
  };

  useEffect(() => {
    const loadMigrationStatus = async () => {
      if (!selectedContract || !isOpen) return;

      setIsLoadingStatus(true);
      const tenantContacts = getTenantContacts(selectedContract);
      const statusMap: { [key: number]: boolean } = {};

      try {
        // Check migration status for each contact
        await Promise.all(
          tenantContacts.map(async (contact) => {
            if (contact.id) {
              const isMigrated = await checkMigrationStatus(contact.id);
              statusMap[contact.id] = isMigrated;
            }
          })
        );
        
        setMigrationStatus(statusMap);
      } catch (error) {
        console.error('Error loading migration status:', error);
        toast.error('Fout bij het laden van migratiestatus');
      } finally {
        setIsLoadingStatus(false);
      }
    };

    loadMigrationStatus();
  }, [selectedContract, isOpen, session]);

  const handleRunMigration = async (contactId: number, showToast=true) => {
    setIsRunningMigration(prev => ({ ...prev, [contactId]: true }));
    
    try {
      const response = await fetchWithAuth(
        `${API_URL}/tenants-migration/create/${contactId}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          }
        },
        session
      );

      if (response.ok) {
        if (showToast) {
          toast.success('Migratie succesvol gestart!');
        }
        return true;
      } else {
        throw new Error('Failed to start migration');
      }
    } catch (error) {
      console.error('Migration error:', error);
      if (showToast) {
      toast.error('Fout bij het starten van de migratie');
      }
      return false;
    } finally {
      setIsRunningMigration(prev => ({ ...prev, [contactId]: false }));
    }
  };

  const handleSendAll = async () => {
    if (!selectedContract) return;

    const tenantContacts = getTenantContacts(selectedContract);
    // Only process contacts that haven't been migrated yet
    const unmiratedContacts = tenantContacts.filter(contact => 
      contact.id && !migrationStatus[contact.id]
    );
    
    if (unmiratedContacts.length === 0) {
      toast.info('Alle contacten zijn al gemigreerd.');
      return;
    }

    setIsSendingAll(true);
    let successCount = 0;
    let failCount = 0;

    try {
      // Process each unmigrated contact sequentially to avoid overwhelming the server
      for (const contact of unmiratedContacts) {
        if (contact.id) {
          const success = await handleRunMigration(contact.id, false);
          if (success) {
            successCount++;
            // Update the migration status for this contact
            setMigrationStatus(prev => ({ ...prev, [contact.id!]: true }));
          } else {
            failCount++;
          }
          // Small delay between requests
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }

      if (successCount > 0) {
        toast.success(`${successCount} migraties succesvol gestart!`);
      }
      if (failCount > 0) {
        toast.error(`${failCount} migraties zijn mislukt.`);
      }
    } catch (error) {
      console.error('Send all migration error:', error);
      toast.error('Fout bij het versturen van alle migraties');
    } finally {
      setIsSendingAll(false);
    }
  };

  if (!selectedContract) return null;

  const tenantContacts = getTenantContacts(selectedContract);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="2xl"
      scrollBehavior="inside"
      backdrop="blur"
    >
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1">
          Huurdersportaal Migratie
          <p className="text-sm text-default-500">
            Selecteer de huurders die gemigreerd moeten worden naar het nieuwe portaal
          </p>
        </ModalHeader>
        <ModalBody>
          <div className="space-y-4">
            <div className="bg-default-50 p-4 rounded-lg">
              <h4 className="font-semibold text-default-700 mb-2">Contract Details</h4>
              <p className="text-sm text-default-600">
                Unit: {selectedContract.unit?.id}
                {selectedContract.unit?.building && (
                  <> - {selectedContract.unit.building.id}</>
                )}
              </p>
            </div>
            
            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-default-700">Huurders</h4>
                {!isLoadingStatus && (
                  <Button
                    color="secondary"
                    size="sm"
                    onPress={handleSendAll}
                    isLoading={isSendingAll}
                    disabled={
                      isSendingAll || 
                      tenantContacts.every(contact => contact.id && migrationStatus[contact.id])
                    }
                    startContent={
                      isSendingAll ? <SpinnerGap className="animate-spin" /> : <PaperPlaneTilt size={16} />
                    }
                  >
                    {isSendingAll ? 'Bezig...' : 'Verstuur Alle'}
                  </Button>
                )}
              </div>
              {tenantContacts.length > 0 ? (
                <div className="space-y-3">
                  {isLoadingStatus ? (
                    <div className="text-center py-8">
                      <SpinnerGap className="animate-spin mx-auto mb-2" size={32} />
                      <p className="text-default-500">Laden van migratiestatus...</p>
                    </div>
                  ) : (
                    tenantContacts.map((contact: Contact) => {
                      const contactId = contact.id;
                      if (!contactId) return null;
                      
                      const isMigrated = migrationStatus[contactId];
                      
                      return (
                        <div
                          key={contactId}
                          className="flex items-center justify-between p-4 border border-default-200 rounded-lg bg-white"
                        >
                          <div className="flex flex-col">
                            <span className="font-medium text-default-800">
                              {contact.firstName} {contact.lastName}
                            </span>
                            <span className="text-sm text-default-500">
                              {contact.email}
                            </span>
                            {contact.phone_mobile && (
                              <span className="text-sm text-default-500">
                                {contact.phone_mobile}
                              </span>
                            )}
                          </div>
                          {isMigrated ? (
                            <Checkbox
                              isSelected={true}
                              isReadOnly
                              size="lg"
                              icon={<Check size={16} />}
                              color="success"
                            >
                              <span className="text-success-600 font-medium">Gemigreerd</span>
                            </Checkbox>
                          ) : (
                            <Button
                              color="success"
                              size="sm"
                              onPress={() => {
                                handleRunMigration(contactId).then((success) => {
                                  if (success) {
                                    // Update migration status after successful migration
                                    setMigrationStatus(prev => ({ ...prev, [contactId]: true }));
                                  }
                                });
                              }}
                              isLoading={!!isRunningMigration[contactId]}
                              disabled={!!isRunningMigration[contactId] || isSendingAll}
                              startContent={
                                isRunningMigration[contactId] ? <SpinnerGap className="animate-spin" /> : undefined
                              }
                            >
                              {isRunningMigration[contactId] ? 'Bezig...' : 'Start Migratie'}
                            </Button>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-default-500">
                  <User size={48} className="mx-auto mb-2 opacity-50" />
                  <p>Geen huurders gevonden voor dit contract</p>
                </div>
              )}
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="danger" variant="light" onPress={onClose}>
            Sluiten
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default MigrationModal;